import PropTypes from "prop-types";
import React from "react";
import { usePrevious } from "hooks/usePrevious";
import { useTileConfig, useVisibleTileEmitter } from "./TileConfigContext";

const VisibleTileContext = React.createContext({
  isActive: false,
  videoId: null,
  visibleTile: { row: 0, column: 0 },
});

export const VisibleTileProvider = React.memo(
  ({
    children,
    isActive,
    videoId,
    initialVisibleTile = { row: 0, column: 0 },
  }) => {
    const [visibleTile, setVisibleTile] = React.useState(initialVisibleTile);

    return (
      <VisibleTileContext.Provider
        value={{ isActive, setVisibleTile, videoId, visibleTile }}
      >
        {React.Children.only(children)}
      </VisibleTileContext.Provider>
    );
  },
);

VisibleTileProvider.propTypes = {
  children: PropTypes.node,
  /**
   * We need to know if a chapter is active so we know which chapter will react
   * to events
   */
  isActive: PropTypes.bool.isRequired,
  videoId: PropTypes.number.isRequired,
  initialVisibleTile: PropTypes.objectOf(
    PropTypes.shape({
      column: PropTypes.number.isRequired,
      row: PropTypes.number.isRequired,
    }).isRequired,
  ),
};

export function useVisibleTile(options = {}) {
  const storyTileConfig = useTileConfig();
  const visibleTileEmitter = useVisibleTileEmitter();

  const {
    videoId: contextualVideoId,
    isActive: contextualIsActive,
    visibleTile,
    setVisibleTile,
  } = React.useContext(VisibleTileContext);

  const videoId = options.videoId ?? contextualVideoId;
  const isActive = options.isActive ?? contextualIsActive;

  const previousIsActive = usePrevious(isActive);

  const { rows, columns } = storyTileConfig[videoId] ?? { rows: 1, columns: 1 };

  React.useEffect(() => {
    if (isActive === true && !previousIsActive) {
      visibleTileEmitter.emit("notifyVideoChange", {
        videoId,
        rows,
        columns,
      });
    }
  }, [columns, isActive, previousIsActive, rows, visibleTileEmitter, videoId]);

  React.useEffect(() => {
    function visibleTileEmitterCallback(tile) {
      // If this tile is within bounds, then set it
      if (tile.column < columns && tile.row < rows) {
        setVisibleTile(tile);
      }
    }

    if (isActive) {
      visibleTileEmitter.addListener(
        "setVisibleTile",
        visibleTileEmitterCallback,
      );
    }

    return () => {
      visibleTileEmitter.removeListener(
        "setVisibleTile",
        visibleTileEmitterCallback,
      );
    };
  }, [columns, isActive, rows, setVisibleTile, visibleTileEmitter]);

  return React.useMemo(
    () =>
      setVisibleTile
        ? { setVisibleTile, tileConfig: { rows, columns }, visibleTile }
        : {
            setVisibleTile: () => {},
            tileConfig: { rows: 1, columns: 1 },
            visibleTile: { row: 0, column: 0 },
          },
    [columns, rows, setVisibleTile, visibleTile],
  );
}
